import { useMemo, useCallback, type ReactNode, type CSSProperties } from 'react';
import { Spin, Empty } from 'antd';
import { getFieldIcon } from '../../../components/Forecast/icons';
import { WindDirectionIcon } from '../../../components/Forecast/WindDirectionIcon';
import { useEventSelection } from '../../../contexts/useEventSelection';
import { useObservationFields } from '../../../hooks/useObservationFields';
import { usePlayerOptional } from '../usePlayerContext';
import { useStationObservations } from '../useStationObservations';
import { useTheme } from '../../../contexts/useTheme';
import {
  resolveStationReadingAtTime,
  getReadingValue,
} from '../stationObservationsUtils';
import type { WidgetProps } from './types';

/** Reference body size matching the top-bar parameter cards. */
const REF_WIDTH = 120;
const REF_HEIGHT = 40;
const UNIT_FONT_RATIO = 0.62;

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

type DisplayParts =
  | { kind: 'wind'; node: ReactNode }
  | { kind: 'text'; text: string }
  | { kind: 'value'; numeric: string; unit: string };

function computeCardMetrics(width: number, height: number) {
  const w = Math.max(width, 1);
  const h = Math.max(height, 1);
  const fitScale = Math.min(w / REF_WIDTH, h / REF_HEIGHT);

  return {
    fitScale,
    labelFontSize: clamp(9 * fitScale, 6, 999),
    valueFontSize: clamp(16 * fitScale, 8, 999),
    iconSize: clamp(16 * fitScale, 10, 999),
    outerPadding: clamp(4 * fitScale, 1, 999),
    cardPaddingV: clamp(4 * fitScale, 2, 999),
    cardPaddingH: clamp(8 * fitScale, 4, 999),
    contentGap: clamp(6 * fitScale, 2, 999),
    labelGap: clamp(2 * fitScale, 1, 8),
    showWindAngle: fitScale >= 1,
    textAreaWidth: Math.max(
      20,
      w - clamp(16 * fitScale, 10, 999) - clamp(6 * fitScale, 2, 999) - clamp(8 * fitScale, 4, 999) * 2 - clamp(4 * fitScale, 1, 999) * 2,
    ),
  };
}

/** Shrink font so text fits within maxWidth (single line). */
function scaleFontToFit(
  text: string,
  baseFontSize: number,
  maxWidth: number,
  charWidthRatio = 0.52,
  minFontSize = 6,
): number {
  if (!text || maxWidth <= 0) return baseFontSize;
  const estimated = text.length * baseFontSize * charWidthRatio;
  if (estimated <= maxWidth) return baseFontSize;
  return clamp(baseFontSize * (maxWidth / estimated), minFontSize, baseFontSize);
}

/** Shrink label font to fit up to two wrapped lines. */
function scaleLabelFont(
  text: string,
  baseFontSize: number,
  maxWidth: number,
  maxLines = 2,
  minFontSize = 6,
): number {
  if (!text || maxWidth <= 0) return baseFontSize;
  const charsPerLine = Math.max(1, maxWidth / (baseFontSize * 0.48));
  const linesNeeded = Math.ceil(text.length / charsPerLine);
  if (linesNeeded <= maxLines) return baseFontSize;
  return clamp(baseFontSize * (maxLines / linesNeeded), minFontSize, baseFontSize);
}

function estimateValueWidth(numeric: string, unit: string, fontSize: number): number {
  const numericWidth = numeric.length * fontSize * 0.56;
  if (!unit) return numericWidth;
  const unitFontSize = fontSize * UNIT_FONT_RATIO;
  const unitWidth = unit.length * unitFontSize * 0.48 + fontSize * 0.12;
  return numericWidth + unitWidth;
}

function scaleValueFont(
  numeric: string,
  unit: string,
  baseFontSize: number,
  maxWidth: number,
  minFontSize = 7,
): number {
  if (maxWidth <= 0) return baseFontSize;
  let size = baseFontSize;
  while (size > minFontSize && estimateValueWidth(numeric, unit, size) > maxWidth) {
    size -= 0.5;
  }
  return size;
}

const centeredStateStyle: CSSProperties = {
  height: '100%',
  width: '100%',
  overflow: 'hidden',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxSizing: 'border-box',
};

export const StationParameterCardWidget = ({
  config,
  pixelSize,
  playerTimestamp,
}: WidgetProps) => {
  const { selectedEvent } = useEventSelection();
  const { getField, getVisibleFields, convertForDisplay, getDisplayUnit, loading: isLoadingFields } =
    useObservationFields();
  const { theme } = useTheme();
  const player = usePlayerOptional();
  const { stations: allStations, loading, error } = useStationObservations();
  const isDark = theme === 'dark';

  const selectedStation = (config.selectedStation as string | undefined) ?? 'avg';
  const fieldName = (config.fieldName as string | undefined) ?? 'temperature';
  const isDemo = selectedEvent?.demo ?? false;

  const selectedDateRange = player?.selectedDateRange ?? null;

  const targetTime = useMemo(() => {
    if (playerTimestamp) return playerTimestamp.getTime();
    if (allStations.length > 0) {
      for (const station of allStations) {
        const last = station.observations[station.observations.length - 1];
        if (last) return new Date(last.reading_timestamp).getTime();
      }
    }
    return Date.now();
  }, [playerTimestamp, allStations]);

  const resolved = useMemo(
    () => resolveStationReadingAtTime(allStations, targetTime, selectedStation),
    [allStations, targetTime, selectedStation],
  );

  const field = useMemo(() => {
    const configured = getField(fieldName);
    if (configured) return configured;
    return getVisibleFields(isDemo).find((f) => f.name === fieldName) ?? null;
  }, [getField, getVisibleFields, fieldName, isDemo]);

  const metrics = useMemo(
    () => computeCardMetrics(pixelSize.width, pixelSize.height),
    [pixelSize.width, pixelSize.height],
  );

  const getReading = useCallback(
    (name: string): number | null => {
      if (!resolved) return null;
      return getReadingValue(resolved, name);
    },
    [resolved],
  );

  const getDisplayParts = useCallback((): DisplayParts => {
    if (!field || !resolved) return { kind: 'text', text: '--' };
    const rawValue = getReading(field.name);
    if (rawValue === null) return { kind: 'text', text: 'N/A' };

    if (field.name === 'windDirection') {
      const windDir = resolved.observation.car_wind_direction;
      if (windDir === null || windDir === undefined) return { kind: 'text', text: 'N/A' };
      const windSpeedValue = getReading('windSpeed');
      const windSpeedKmh =
        windSpeedValue !== null ? windSpeedValue * 3.6 : null;
      return {
        kind: 'wind',
        node: (
          <WindDirectionIcon
            direction={windDir}
            windSpeed={windSpeedKmh}
            size={metrics.iconSize}
            showAngle={metrics.showWindAngle}
          />
        ),
      };
    }

    const displayValue = convertForDisplay(field.name, rawValue);
    if (displayValue === null) return { kind: 'text', text: 'N/A' };

    if (
      (field.name === 'windGust' && displayValue <= 0) ||
      ((field.name === 'rainIntensity' || field.name === 'precipitationIntensity') &&
        displayValue === 0)
    ) {
      return { kind: 'text', text: 'N/A' };
    }

    const unit = getDisplayUnit(field.name) || field.unit || '';
    let decimals = 1;
    if (unit === '%' || unit === 'lux' || unit === 'Lx') decimals = 0;

    const numeric =
      unit === 'lux' || unit === 'Lx'
        ? displayValue.toLocaleString('en-US', { maximumFractionDigits: 0 })
        : displayValue.toFixed(decimals);

    return { kind: 'value', numeric, unit };
  }, [
    field,
    resolved,
    getReading,
    convertForDisplay,
    getDisplayUnit,
    metrics.iconSize,
    metrics.showWindAngle,
  ]);

  const stationIndicator =
    selectedStation === 'avg' ? '*' : resolved?.stationReference ?? selectedStation;

  const cardBg = isDark ? '#1f1f1f' : '#f5f5f5';
  const cardBorder = isDark
    ? '1px solid rgba(255, 255, 255, 0.1)'
    : '1px solid #d9d9d9';
  const labelColor = isDark
    ? 'rgba(255, 255, 255, 0.65)'
    : 'rgba(0, 0, 0, 0.65)';
  const valueColor = isDark ? 'white' : 'black';

  if ((loading || isLoadingFields) && allStations.length === 0) {
    return (
      <div style={centeredStateStyle}>
        <Spin size="small" />
      </div>
    );
  }

  if (!selectedEvent) {
    return (
      <div style={centeredStateStyle}>
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={<span style={{ fontSize: 11 }}>No event selected</span>} />
      </div>
    );
  }

  if (!selectedDateRange) {
    return (
      <div style={centeredStateStyle}>
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={<span style={{ fontSize: 11 }}>Select a time range on the timeline</span>}
        />
      </div>
    );
  }

  if (error || allStations.length === 0) {
    return (
      <div style={centeredStateStyle}>
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={<span style={{ fontSize: 11 }}>{error || 'No station data available'}</span>}
        />
      </div>
    );
  }

  if (!field) {
    return (
      <div style={centeredStateStyle}>
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={<span style={{ fontSize: 11 }}>Select a parameter in widget settings</span>}
        />
      </div>
    );
  }

  if (!resolved) {
    return (
      <div style={centeredStateStyle}>
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={<span style={{ fontSize: 11 }}>No observations found</span>}
        />
      </div>
    );
  }

  const {
    labelFontSize: baseLabelFontSize,
    valueFontSize: baseValueFontSize,
    iconSize,
    outerPadding,
    cardPaddingV,
    cardPaddingH,
    contentGap,
    labelGap,
    textAreaWidth,
  } = metrics;

  const displayParts = getDisplayParts();
  const labelWidth = Math.max(16, textAreaWidth - 10);
  const labelFontSize = scaleLabelFont(field.display_name, baseLabelFontSize, labelWidth);
  const valueFontSize =
    displayParts.kind === 'value'
      ? scaleValueFont(displayParts.numeric, displayParts.unit, baseValueFontSize, textAreaWidth)
      : displayParts.kind === 'text'
        ? scaleFontToFit(displayParts.text, baseValueFontSize, textAreaWidth)
        : baseValueFontSize;

  const renderValue = () => {
    if (displayParts.kind === 'wind') return displayParts.node;
    if (displayParts.kind === 'text') return displayParts.text;
    return (
      <>
        <span style={{ whiteSpace: 'nowrap' }}>{displayParts.numeric}</span>
        {displayParts.unit ? (
          <span
            style={{
              fontSize: `${UNIT_FONT_RATIO}em`,
              fontWeight: 500,
              whiteSpace: 'nowrap',
              marginLeft: '0.12em',
              opacity: 0.85,
            }}
          >
            {displayParts.unit}
          </span>
        ) : null}
      </>
    );
  };

  return (
    <div
      style={{
        height: '100%',
        width: '100%',
        padding: outerPadding,
        boxSizing: 'border-box',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          height: '100%',
          width: '100%',
          background: cardBg,
          border: cardBorder,
          borderRadius: 6,
          padding: `${cardPaddingV}px ${cardPaddingH}px`,
          boxSizing: 'border-box',
          display: 'flex',
          alignItems: 'center',
          gap: contentGap,
          overflow: 'hidden',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
          {getFieldIcon(field.name, { size: iconSize })}
        </div>

        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            flex: 1,
            minWidth: 0,
            minHeight: 0,
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              width: '100%',
              fontSize: labelFontSize,
              fontWeight: 500,
              color: labelColor,
              lineHeight: 1.15,
              marginBottom: labelGap,
              gap: 4,
              flexShrink: 0,
            }}
          >
            <span
              title={field.display_name}
              style={{
                flex: 1,
                minWidth: 0,
                overflow: 'hidden',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                wordBreak: 'break-word',
              }}
            >
              {field.display_name}
            </span>
            <span style={{ opacity: 0.8, flexShrink: 0, lineHeight: 1 }}>{stationIndicator}</span>
          </div>

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 4,
              fontSize: valueFontSize,
              fontWeight: 600,
              color: valueColor,
              lineHeight: 1,
              flex: 1,
              minHeight: 0,
              minWidth: 0,
              overflow: 'hidden',
              whiteSpace: 'nowrap',
            }}
          >
            {renderValue()}
            {loading && <Spin size="small" />}
          </div>
        </div>
      </div>
    </div>
  );
};
